import { NavLink, useLocation } from 'react-router-dom';
import { DatabaseIcon } from 'lucide-react';
import { DexNavItem } from '@dexmate/dexui';
import { NAV_GROUPS } from '@/lib/nav-items';

interface SidebarProps {
  /** Mobile drawer mode: render as a panel; otherwise as a sticky aside. */
  mobile?: boolean;
  /** Called when a nav item is clicked (used by mobile to close the drawer). */
  onNavigate?: () => void;
}

/**
 * Sidebar — persistent left navigation. Mirrors dexhub's `layouts/nav/SidebarNav.tsx`
 * shape: logo at the top, grouped link list, footer chip at the bottom.
 *
 * Active state: react-router's `useLocation` resolves the current pathname;
 * a link is active when pathname === href OR pathname starts with `${href}/`.
 */
export function Sidebar({ mobile = false, onNavigate }: SidebarProps) {
  const { pathname } = useLocation();

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  return (
    <aside
      className={[
        'flex flex-col gap-4 border-r border-dex-border bg-dex-bg-elevated',
        mobile
          ? 'h-full w-64 px-4 py-4'
          : 'sticky top-0 hidden h-screen w-60 shrink-0 px-3 py-4 lg:flex',
      ].join(' ')}
      aria-label="Primary navigation"
    >
      {/* SidebarLogo */}
      <div className="flex items-center gap-2 px-2 pb-2">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-dex-accent text-white">
          <DatabaseIcon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold tracking-tight text-dex-text-primary">
            cloud-data-service
          </div>
          <div className="text-[10px] uppercase tracking-wider text-dex-status-info">
            DEV
          </div>
        </div>
      </div>

      {/* SidebarNav */}
      <nav className="flex-1 space-y-5 overflow-y-auto" aria-label="Sections">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-dex-text-hint">
              {group.label}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.href}>
                    {/* react-router's NavLink keeps the native anchor semantics
                        (so middle-click and copy-link work) while letting us
                        handle SPA navigation without a full reload. We render
                        a DexNavItem inside as a styling host.
                        Note: we set `active` from useLocation rather than
                        NavLink's built-in className to keep the active style
                        consistent with what dexhub uses. */}
                    <NavLink
                      to={item.href}
                      onClick={onNavigate}
                      className="block focus:outline-none rounded-md focus-visible:ring-2 focus-visible:ring-dex-accent"
                    >
                      <DexNavItem
                        icon={<item.icon />}
                        label={item.label}
                        active={active}
                        badge={item.wip ? 'WIP' : undefined}
                      />
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* SidebarFooter — small env reminder. Role chip lives in the top
          AppHeader (right side) per Pass 2B. */}
      <div className="border-t border-dex-border pt-3 text-[11px] text-dex-text-hint">
        api.data-dev.dexmate.us
      </div>
    </aside>
  );
}
